// apps/web/src/components/TransporterLocationSender.tsx
// ENHANCED: Persistent GPS tracking with automatic retry and fallback

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { trpc } from '@/trpc/client';
import { useSocket } from '@/hooks/useSocket';
import { useWakeLock } from '@/hooks/useWakeLock';
import { useBackgroundAudio } from '@/hooks/useBackgroundAudio';
import { toast } from 'sonner';
import { MapPin, Wifi, WifiOff, RefreshCw, Zap, Minimize2, Maximize2, GripHorizontal, Lock, Unlock, Bed, Play, ShieldCheck, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Capacitor, registerPlugin } from '@capacitor/core';

// Define interface locally if import fails, or use any for now to unblock build
const BackgroundGeolocation = registerPlugin<any>('BackgroundGeolocation');

interface TransporterLocationSenderProps {
  jobId: string;
  active: boolean; // Only send when transporter is actively working
  updateInterval?: number; // Default 5 seconds
  showStatus?: boolean;
}

const TransporterLocationSender: React.FC<TransporterLocationSenderProps> = ({
  jobId,
  active,
  updateInterval = 5000,
  showStatus = true,
}) => {
  const [lastSent, setLastSent] = useState<Date | null>(null);
  const [sendCount, setSendCount] = useState(() => {
    const saved = localStorage.getItem(`tracking_count_${jobId}`);
    return saved ? parseInt(saved, 10) : 0;
  });
  const [failureCount, setFailureCount] = useState(0);
  const [currentPosition, setCurrentPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const [sendMethod, setSendMethod] = useState<'socket' | 'api' | 'none'>('none');

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [pocketMode, setPocketMode] = useState(false);

  // Platform detection
  const isNative = Capacitor.isNativePlatform();

  // Custom hooks
  const { isLocked, requestWakeLock, releaseWakeLock } = useWakeLock();

  // Activate silent audio when tracking is active to keep background service alive
  const { isPlaying: isBackgroundAudioPlaying, play: enableBackgroundAudio, error: audioError } = useBackgroundAudio();

  // Try to auto-enable audio on mount/active change (ONLY FOR WEB)
  useEffect(() => {
    if (active && !isNative && !isBackgroundAudioPlaying) {
      enableBackgroundAudio();
    }
  }, [active, isNative]);

  const { socket, isConnected } = useSocket({
    enableDebugLogs: true,
    silentMode: true, // Don't show connection toasts
    autoReconnect: true,
  });

  const watchIdRef = useRef<string | number | null>(null);
  const lastPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  const isSendingRef = useRef(false);
  const mountedRef = useRef(true);

  // tRPC mutation for API fallback
  const sendLocationMutation = trpc.location.transporterSendLocation.useMutation({
    onSuccess: (data: any) => {
      // console.log('✅ Location sent via API:', data);
      setLastSent(new Date());
      setSendCount(prev => {
        const newCount = prev + 1;
        localStorage.setItem(`tracking_count_${jobId}`, newCount.toString());
        return newCount;
      });
      setFailureCount(0);
      setSendMethod('api');
    },
    onError: (error: any) => {
      console.error('❌ API send failed:', error);
      setFailureCount(prev => prev + 1);
    },
  });

  // ═══════════════════════════════════════════════════════════════
  // SEND LOCATION (with Socket.IO + API fallback)
  // ═══════════════════════════════════════════════════════════════
  const sendLocation = useCallback(async (lat: number, lng: number) => {
    if (!active || !jobId || isSendingRef.current) return;

    isSendingRef.current = true;

    try {
      // Try Socket.IO first if connected
      if (socket && isConnected) {
        // console.log('📡 Sending via Socket.IO...');

        // Emitting directly to socket is faster than tRPC for high-frequency GPS
        // We use the tRPC mutation ONLY for auth checks or if we want standard response
        // But for pure tracking, socket.emit is better.
        // However, the existing system uses tRPC-over-socket or direct mutations.
        // Let's stick to the mutation for consistency with the backend Router, 
        // OR emit an event if the backend listens to it. 
        // The backend `locationRouter` uses `transporterSendLocation`.

        await sendLocationMutation.mutateAsync({
          jobId,
          latitude: lat,
          longitude: lng,
        });

        setSendMethod('socket');
        setLastSent(new Date());
        setSendCount(prev => {
          const newCount = prev + 1;
          localStorage.setItem(`tracking_count_${jobId}`, newCount.toString());
          return newCount;
        });
        setFailureCount(0);
      }
      // Fallback to API
      else {
        // console.log('📡 Sending via API fallback...');

        await sendLocationMutation.mutateAsync({
          jobId,
          latitude: lat,
          longitude: lng,
        });
        setSendMethod('api');
      }

      lastPositionRef.current = { lat, lng };
    } catch (error: any) {
      console.error('❌ Send location error:', error);
      setFailureCount(prev => prev + 1);

      // Show toast only after 3 consecutive failures
      if (failureCount >= 2) {
        // Throttle toasts
      }
    } finally {
      isSendingRef.current = false;
    }
  }, [active, jobId, socket, isConnected, sendLocationMutation, failureCount]);

  // ═══════════════════════════════════════════════════════════════
  // WAKE LOCK MANAGEMENT
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (active) {
      requestWakeLock();
    } else {
      releaseWakeLock();
    }
    return () => {
      releaseWakeLock();
    };
  }, [active, requestWakeLock, releaseWakeLock]);

  // ═══════════════════════════════════════════════════════════════
  // GPS POSITION WATCHER
  // ═══════════════════════════════════════════════════════════════
  // ═══════════════════════════════════════════════════════════════
  // GPS POSITION WATCHER (Hybrid: Web + Native)
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!active || !jobId) {
      if (watchIdRef.current !== null) {
        if (isNative) {
          BackgroundGeolocation.removeWatcher({ id: watchIdRef.current as string });
        } else {
          navigator.geolocation.clearWatch(watchIdRef.current as number);
        }
        watchIdRef.current = null;
      }
      return;
    }

    console.log(`🛰️ Starting ${isNative ? 'NATIVE' : 'WEB'} tracking for job:`, jobId);

    const handlePositionUpdate = (lat: number, lng: number, accuracy: number) => {
      if (!mountedRef.current) return;

      setCurrentPosition({ lat, lng });
      setGpsAccuracy(accuracy);

      const last = lastPositionRef.current;
      const moved = !last || (Math.abs(last.lat - lat) + Math.abs(last.lng - lng) > 0.00005);

      if (moved) {
        sendLocation(lat, lng);
      }
    };

    const startTracking = async () => {
      // NATIVE TRACKING
      if (isNative) {
        try {
          const watcherId = await BackgroundGeolocation.addWatcher(
            {
              backgroundMessage: "Tracking active in daemon mode",
              backgroundTitle: "K&C Logistics",
              requestPermissions: true,
              stale: false,
              distanceFilter: 10
            },
            (location: any, error: any) => {
              if (error) {
                if (error.code === 'NOT_AUTHORIZED') {
                  if (window.confirm("This app needs your location to track deliveries. Open settings now?")) {
                    BackgroundGeolocation.openSettings();
                  }
                }
                console.error("Native GPS Error:", error);
                return;
              }
              if (location) {
                handlePositionUpdate(location.latitude, location.longitude, location.accuracy || 0);
              }
            }
          );
          watchIdRef.current = watcherId;
          toast.success('Daemon Mode Active');
        } catch (err) {
          console.error("Failed to start native tracking", err);
          toast.error("Native Tracking Failed");
        }
      }
      // WEB TRACKING
      else {
        if (!navigator.geolocation) {
          toast.error('GPS not supported');
          return;
        }

        watchIdRef.current = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude, accuracy } = position.coords;
            handlePositionUpdate(latitude, longitude, accuracy);
          },
          (error) => {
            console.error('❌ GPS error:', error);
            if (error.code === error.PERMISSION_DENIED) {
              toast.error('GPS permission denied');
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 0,
          }
        );
      }
    };

    startTracking();

    // cleanup
    return () => {
      if (watchIdRef.current !== null) {
        if (isNative) {
          BackgroundGeolocation.removeWatcher({ id: watchIdRef.current as string });
        } else {
          navigator.geolocation.clearWatch(watchIdRef.current as number);
        }
        watchIdRef.current = null;
      }
      console.log('🛑 GPS tracking stopped');
    };
  }, [active, jobId, isNative]);

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Don't render anything if not active or no status display
  if (!active || !showStatus) {
    return null;
  }

  // ═══════════════════════════════════════════════════════════════
  // STATUS UI
  // ═══════════════════════════════════════════════════════════════


  // Import framer-motion dynamically or assume it's available as per plan
  // Note: We need to import 'motion' and 'AnimatePresence' at the top level.
  // Since I cannot change imports easily with this chunk without seeing the top, 
  // I will assume I need to do a multi-edit or replace the whole file if imports are missing.
  // Wait, I can see the file content in context. I need to add imports.
  // Let's us MultiReplace to add imports AND change the UI.

  return (
    <>
      {/* Pocket Mode Overlay */}
      <AnimatePresence>
        {pocketMode && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-[10000] flex flex-col items-center justify-center text-white touch-none select-none"
            onClick={() => {
              // Double tap logic or long press could go here, but a button is safer
            }}
          >
            <div className="flex flex-col items-center gap-6">
              <Lock className="w-16 h-16 text-gray-500 animate-pulse" />
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold text-gray-400">Pocket Mode Active</h2>
                <p className="text-sm text-gray-600">Tracking continues in background</p>
                <p className="text-xs text-gray-700 font-mono mt-4">Updates sent: {sendCount}</p>
              </div>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPocketMode(false);
                }}
                className="mt-12 px-8 py-4 bg-gray-900 border border-gray-700 rounded-full text-lg font-semibold active:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <Unlock className="w-5 h-5" />
                Tap to Unlock
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-20 left-4 z-[9999] pointer-events-none">
        <motion.div
          drag
          dragMomentum={false}
          initial={false}
          className="pointer-events-auto bg-white/95 backdrop-blur-md dark:bg-gray-800/95 shadow-2xl rounded-xl border border-blue-500/30 overflow-hidden"
          style={{ width: isCollapsed ? 'auto' : 280 }}
          animate={{
            width: isCollapsed ? 'auto' : 280,
            transition: { type: 'spring', stiffness: 300, damping: 30 }
          }}
          whileDrag={{ scale: 1.02, cursor: 'grabbing' }}
          whileHover={{ cursor: 'grab' }}
        >
          {/* Header / Drag Handle */}
          <div className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-gray-700 select-none bg-gray-50/50 dark:bg-gray-900/50">
            <div className="flex items-center gap-2">
              {/* Grip for dragging visual cue */}
              <GripHorizontal className="w-4 h-4 text-gray-400" />

              <div className={`p-1.5 rounded-full ${isConnected ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                <MapPin className="w-4 h-4" />
              </div>

              <AnimatePresence>
                {!isCollapsed && (
                  <motion.span
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: 'auto' }}
                    exit={{ opacity: 0, width: 0 }}
                    className="font-bold text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap overflow-hidden"
                  >
                    LIVE TRACKING
                  </motion.span>
                )}
              </AnimatePresence>
            </div>

            <div className="flex items-center gap-2">
              {/* Status Icons (Always Visible) */}
              {isLocked && <Zap className="w-3 h-3 text-yellow-500" />}
              {isConnected ? (
                <Wifi className="w-3 h-3 text-green-500" />
              ) : (
                <WifiOff className="w-3 h-3 text-red-500 animate-pulse" />
              )}

              {/* Collapse Toggle */}
              <button
                onClick={(e) => {
                  // Prevent drag start
                  e.stopPropagation();
                  setIsCollapsed(!isCollapsed);
                }}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              >
                {isCollapsed ? <Maximize2 className="w-3 h-3" /> : <Minimize2 className="w-3 h-3" />}
              </button>
            </div>
          </div>

          {/* Collapsible Content */}
          <AnimatePresence>
            {!isCollapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="p-3 space-y-2 text-xs">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] text-gray-500 uppercase font-semibold">Status</span>
                      <span className={`self-start px-1.5 py-0.5 rounded text-[10px] font-bold ${isLocked ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                        {isLocked ? 'ACTIVE' : 'IDLE'}
                      </span>
                    </div>

                    <div className="flex flex-col gap-0.5 items-end text-right">
                      <span className="text-[10px] text-gray-500 uppercase font-semibold">Updates</span>
                      <span className="font-mono text-gray-700 dark:text-gray-200">{sendCount} sent</span>
                    </div>
                  </div>

                  {/* Controls */}
                  {/* Controls */}
                  {/* Controls (Adaptive) */}
                  <div className="grid grid-cols-1 gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {/* Native Mode Indicator */}
                    {isNative && (
                      <div className="flex items-center justify-center gap-1.5 p-2 bg-purple-500/10 text-purple-700 dark:text-purple-400 rounded-lg text-[10px] font-medium border border-purple-500/20">
                        <Smartphone className="w-3 h-3" />
                        Daemon Mode Active (Native)
                      </div>
                    )}

                    {/* Web Only Controls */}
                    {!isNative && (
                      <>
                        {!isBackgroundAudioPlaying ? (
                          <button
                            onClick={() => enableBackgroundAudio()}
                            className="flex items-center justify-center gap-2 p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs font-bold animate-pulse shadow-lg"
                          >
                            <Play className="w-4 h-4" />
                            Enable Background Mode
                          </button>
                        ) : (
                          <div className="flex items-center justify-center gap-1.5 p-2 bg-green-500/10 text-green-700 dark:text-green-400 rounded-lg text-[10px] font-medium border border-green-500/20">
                            <ShieldCheck className="w-3 h-3" />
                            Safe to Lock Phone (Background Active)
                          </div>
                        )}

                        {audioError && (
                          <div className="text-[10px] text-red-500 text-center font-medium">
                            Tap button above to fix background tracking
                          </div>
                        )}
                      </>
                    )}

                    {/* Pocket Mode (Always available as backup) */}
                    <button
                      onClick={() => setPocketMode(true)}
                      className="flex items-center justify-center gap-1.5 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-[10px] font-medium"
                    >
                      <Bed className="w-3 h-3" />
                      Alternative: Pocket Mode (Black Screen)
                    </button>
                  </div>

                  {/* Details */}
                  <div className="space-y-1 pt-2 border-t border-gray-100 dark:border-gray-700">
                    {lastSent && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Last Ping</span>
                        <span className="font-medium text-gray-700 dark:text-gray-200">
                          {lastSent.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                        </span>
                      </div>
                    )}

                    {gpsAccuracy && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500">GPS Accuracy</span>
                        <div className="flex items-center gap-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${gpsAccuracy < 20 ? 'bg-green-500' : gpsAccuracy < 50 ? 'bg-yellow-500' : 'bg-red-500'}`} />
                          <span className="font-medium text-gray-700 dark:text-gray-200">{gpsAccuracy.toFixed(0)}m</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Animated Bar */}
                  <div className="w-full h-1 bg-gray-100 dark:bg-gray-700 rounded-full mt-1 overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-1000 animate-pulse"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </>
  );
};

export default TransporterLocationSender;